import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Users, Loader2 } from 'lucide-react';
import { ClientLicense, LicensePlan } from '@/types/license';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ClientLicenseWithPlan extends ClientLicense {
  license_plan?: LicensePlan;
}

interface LicenseUsageCardProps {
  license: ClientLicenseWithPlan | null;
}

// Roles que consomem licença (seats)
const LICENSED_ROLES = ['technician', 'client_admin'];

const LicenseUsageCard = ({ license }: LicenseUsageCardProps) => {
  // Buscar contagem real de usuários que consomem licença
  const { data: usedSeats, isLoading } = useQuery({
    queryKey: ['license-seats-count', license?.client_id],
    queryFn: async () => {
      if (!license?.client_id) return 0;

      const { data: allUsers, error } = await supabase
        .from('users')
        .select('role, client_id, client_ids')
        .eq('status', 'active');

      if (error) throw error;

      // Filtrar usuários que têm acesso a este cliente via client_id ou client_ids
      const clientUsers = allUsers?.filter(user => {
        if (user.client_id === license.client_id) return true;
        if (user.client_ids && Array.isArray(user.client_ids)) {
          return user.client_ids.includes(license.client_id);
        }
        return false;
      }) || [];

      // Contar apenas roles que consomem licença (excluindo admin e super_admin)
      const licensedUsers = clientUsers.filter(u => LICENSED_ROLES.includes(u.role));
      
      return licensedUsers.length;
    },
    enabled: !!license?.client_id,
  });

  if (!license) return null;

  // Quantidade de licenças contratadas (fallback para 1)
  const contractedSeats = license.contracted_seats ?? 1;
  const currentSeats = usedSeats ?? 0;
  
  const usagePercentage = contractedSeats > 0 
    ? Math.min((currentSeats / contractedSeats) * 100, 100) 
    : 0;
  
  const isOverLimit = currentSeats > contractedSeats;
  const isAtLimit = currentSeats === contractedSeats;

  const getProgressColor = (percentage: number) => {
    if (isOverLimit || percentage >= 100) return 'bg-red-500';
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-amber-500';
    return 'bg-green-500';
  };

  const getStatusMessage = () => {
    if (isOverLimit) {
      return `Limite excedido (${currentSeats - contractedSeats} licença(s) a mais)`;
    }
    if (isAtLimit) {
      return 'Limite atingido';
    }
    if (usagePercentage >= 75) {
      return 'Próximo do limite';
    }
    return `${Math.round(usagePercentage)}% utilizado`;
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          Licenças em Uso
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex justify-between text-2xl font-bold">
            {isLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <span className={isOverLimit ? 'text-red-500' : undefined}>
                {currentSeats}
              </span>
            )}
            <span className="text-muted-foreground text-base font-normal">
              / {contractedSeats}
            </span>
          </div>
          <Progress 
            value={usagePercentage} 
            className="h-2"
            // @ts-ignore
            indicatorClassName={getProgressColor(usagePercentage)}
          />
          <p className={`text-xs ${isOverLimit || isAtLimit ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
            {getStatusMessage()}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default LicenseUsageCard;
